import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'store';

import BottomSheet from 'components/BottomSheet';
import Button from 'components/Buttons/Button';
import Loader from 'components/Loader';
import Shimmer from 'components/Shimmer';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { UpSellInclusionType, UpSellOption } from 'models/upsell';
import { getFormattedPrice } from 'utils/currency';
import { firstCapitalize } from 'utils/string';

import PolicyTag from '../PolicyTag';
import { NoIcon, YesIcon } from '../UpsellDesktop/styles';
import {
  Close,
  Header,
  Content,
  Box,
  Title,
  Price,
  ChargeIcon,
  Icon,
  LoaderContainer,
  PrevButton,
  NextButton,
} from './styles';

interface Props {
  show: boolean;
  onClose: () => void;
  onSelect: (optionId: number) => void;
  options: UpSellOption[];
  loading: boolean;
}

const IconType = ({ type }: { type: UpSellInclusionType }) =>
  type === UpSellInclusionType.AtCharge ? (
    <ChargeIcon />
  ) : type === UpSellInclusionType.Included ? (
    <YesIcon />
  ) : (
    <NoIcon />
  );

const UpSellMobile = ({ show, onClose, onSelect, options, loading }: Props) => {
  const { t } = useStoreTranslation();
  const { currency } = useSelector((state: RootState) => state.i18n.country);
  const formattedPrice = (value: number | undefined) => {
    const { formatted } = getFormattedPrice({ value, currency, t });
    return formatted;
  };
  const contentRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const [prevEnabled, setPrevEnabled] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [scrollPos, setScrollPos] = useState(0);

  useEffect(() => {
    setNextEnabled(false);
    setPrevEnabled(false);
    if (contentRef.current && slideRef.current && !loading) {
      if (contentRef.current.offsetWidth < slideRef.current.scrollWidth) {
        setNextEnabled(true);
      }
      if (scrollPos > 0) {
        setPrevEnabled(true);
      } else {
        setPrevEnabled(false);
      }
      if (scrollPos < slideRef.current.scrollWidth - contentRef.current.offsetWidth) {
        setNextEnabled(true);
      } else {
        setNextEnabled(false);
      }
    }
  }, [contentRef, options, scrollPos, loading]);

  return (
    <BottomSheet show={show} onClose={onClose} noHeader noScroll>
      {loading ? (
        <>
          <Header>
            <div>
              <Header.Title>
                <Shimmer>Customize your rate</Shimmer>
              </Header.Title>
              <Header.Sub>
                <Shimmer>Select the rate that best suits you on your trip.</Shimmer>
              </Header.Sub>
            </div>
          </Header>
          <LoaderContainer>
            <Loader fullScreen={false} />
          </LoaderContainer>
        </>
      ) : (
        <>
          <Header>
            <div>
              <Header.Title>{t('upsellFares.title')}</Header.Title>
              <Header.Sub>{t('upsellFares.subtitle')}</Header.Sub>
            </div>
            <Close onClick={onClose}>
              <Close.Icon />
            </Close>
          </Header>
          <Content ref={contentRef}>
            <Content.Slide ref={slideRef}>
              {options &&
                options.map((o, k) => (
                  <>
                    <Box key={k}>
                      <Box.Header>
                        <Title>{o.families[0].name.toUpperCase()}</Title>
                        <Price>{formattedPrice(o.amount.fare + o.amount.taxes)}</Price>
                      </Box.Header>
                      <Box.Content>
                        <Box.Category>
                          <PolicyTag isPreApproved={!!o.isPreApproved} breakRules={o.breakRulesMessage || []} />
                        </Box.Category>
                        {o.families[0].inclusions.map((inc, iidx) => (
                          <Box.Category key={`${k}-${iidx}`}>
                            <Icon>
                              <IconType type={inc.type as UpSellInclusionType} />
                            </Icon>
                            <Content.Category>
                              <Content.Title>{inc.rowName && firstCapitalize(inc.rowName)}</Content.Title>
                              <Content.Subtitle>
                                {inc.title ? firstCapitalize(inc.title) : ''}
                                {inc.subtitle ? firstCapitalize(inc.subtitle) : ''}
                              </Content.Subtitle>
                            </Content.Category>
                          </Box.Category>
                        ))}
                      </Box.Content>
                      <Box.Button>
                        <Button
                          onClick={() => {
                            onSelect(o.optionId);
                          }}
                        >
                          {t('upsellFares.select')}
                        </Button>
                      </Box.Button>
                    </Box>
                  </>
                ))}
            </Content.Slide>
          </Content>
          {prevEnabled && (
            <PrevButton
              onClick={() => {
                if (slideRef.current) {
                  const pos = slideRef.current.scrollLeft - slideRef.current.offsetWidth;
                  slideRef.current.scrollTo({ left: pos, top: 0, behavior: 'smooth' });
                  setScrollPos(pos);
                }
              }}
            >
              <PrevButton.Icon />
            </PrevButton>
          )}

          {nextEnabled && (
            <NextButton
              onClick={() => {
                if (slideRef.current) {
                  const pos = slideRef.current.scrollLeft + slideRef.current.offsetWidth;
                  slideRef.current.scrollTo({ left: pos, top: 0, behavior: 'smooth' });
                  setScrollPos(pos);
                }
              }}
            >
              <NextButton.Icon />
            </NextButton>
          )}
        </>
      )}
    </BottomSheet>
  );
};
export default UpSellMobile;
